'use client';

import { useMemo } from 'react';
import {
  isAllResortsParam,
} from '@/utils/resort-search-params';
import {
  DefaultAdults,
  DefaultChildren,
  useSearch,
} from '@/context/listing-search-provider';
import { NodeResort } from '@/utils/drupal-api/__generated__/graphql';
import { ListingsFacets } from '@/utils/platform-api/__generated__/graphql';
import { createSearchDateLabel } from '@/utils/create-search-date-label';
import clsx from 'clsx';
import { ClientBrandConfig } from 'types/brand-config';

import { FiltersButton } from '@/components/filter/filters-button';
import {
  CalendarIcon,
  GuestsIcon,
  PinIcon,
} from '@/components/search/icons';
import { formatFlexDaysLabel } from '@/components/search/utils/format-flex-days-label';
import { formatGuestsLabel } from '@/components/search/utils/format-guests-label';
import { SEARCH_CONFIG } from '@/components/search/search-config';
import Button from '@/components/ui/button';

const collapsedWhenIconClass =
  '!size-3.5 shrink-0 text-primary-main [&_path]:stroke-[1.5]';
const collapsedMetaIconClass =
  '!size-3 shrink-0 text-primary-main [&_path]:stroke-[1.5]';

type CollapsedSearchButtonProps = {
  brand: ClientBrandConfig;
  onExpand: () => void;
  resorts?: NodeResort[];
  listingFacets?: ListingsFacets;
  submitPath?: string;
  className?: string;
  lockedFilterKeys?: SearchFiltersValues[];
};

export const CollapsedSearchButton = ({
  brand,
  onExpand,
  resorts,
  listingFacets,
  submitPath,
  className,
  lockedFilterKeys,
}: CollapsedSearchButtonProps) => {
  const { selectedDates, adults, children, resort, flexDays, guestsInteracted } =
    useSearch();
  const { showPropertyListOnSearch, showResortOnSearch } = brand;
  const hasWhere =
    showPropertyListOnSearch || (showResortOnSearch && Boolean(resorts));

  const resortLabel = useMemo(() => {
    if (!resort || !resorts || isAllResortsParam(resort)) return undefined;
    return resorts.find((r) => r.externalId === resort)?.title || resort;
  }, [resort, resorts]);

  const whenLabel = useMemo(() => {
    const hasDates = Boolean(selectedDates?.from && selectedDates?.to);

    if (!hasDates) {
      return 'Any Dates';
    }

    const dateLabel = createSearchDateLabel(
      selectedDates!.from!,
      selectedDates!.to!
    );
    const flexLabel = formatFlexDaysLabel(flexDays);

    return flexLabel ? `${dateLabel} ${flexLabel}` : dateLabel;
  }, [selectedDates, flexDays]);

  const guestLabel = useMemo(() => {
    if (!guestsInteracted) {
      return 'Add Guests';
    }

    const effectiveAdults = adults ?? DefaultAdults;
    const effectiveChildren = children ?? DefaultChildren;

    return formatGuestsLabel(effectiveAdults, effectiveChildren);
  }, [adults, children, guestsInteracted]);

  const whereLabel = useMemo(() => {
    if (!hasWhere) {
      return undefined;
    }

    if (resortLabel) {
      return resortLabel as string;
    }

    if (isAllResortsParam(resort)) {
      return `All ${brand.resortPluralLabel || 'Resorts'}`;
    }

    if (showPropertyListOnSearch) {
      return 'All Properties';
    }

    return undefined;
  }, [
    brand.resortPluralLabel,
    hasWhere,
    resort,
    resortLabel,
    showPropertyListOnSearch,
  ]);

  const hasFilters = Boolean(brand.filterCategories);

  return (
    <div
      className={clsx(
        'flex w-full min-w-0 items-stretch overflow-hidden rounded-[0.75rem] bg-white !shadow-md',
        SEARCH_CONFIG.mobileBorder,
        className
      )}
    >
      <Button
        type="button"
        style="text"
        normalCase
        className={clsx(
          'h-auto min-h-12 min-w-0 flex-1 justify-start overflow-hidden bg-white px-4 py-2.5 text-left !shadow-none hover:bg-white',
          '[&>div]:w-full [&>div]:min-w-0 [&>div>span]:block [&>div>span]:w-full [&>div>span]:min-w-0'
        )}
        onClick={onExpand}
      >
        <div className="flex w-full flex-col items-start gap-1">
          <div className="flex w-full min-w-0 items-center gap-1.5">
            <CalendarIcon className={collapsedWhenIconClass} />
            <span className="ut-sm min-w-0 flex-1 truncate font-semibold text-black-1">
              {whenLabel}
            </span>
          </div>
          <div className="ut-xs flex w-full min-w-0 items-center gap-1.5 font-normal text-gray-7">
            <GuestsIcon className={collapsedMetaIconClass} />
            <span className="shrink-0">{guestLabel}</span>
            {whereLabel && (
              <>
                <span className="shrink-0" aria-hidden="true">
                  ·
                </span>
                <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                  <PinIcon className={collapsedMetaIconClass} />
                  <span className="min-w-0 truncate">{whereLabel}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </Button>
      {hasFilters && (
        <FiltersButton
          brand={brand}
          listingFacets={listingFacets}
          submitPath={submitPath}
          className="border-l border-gray-2"
          lockedFilterKeys={lockedFilterKeys}
        />
      )}
    </div>
  );
};
